import type { QueryObserverOptions, QueryObserverResult } from "@tanstack/vue-query";
import type { Ref } from "vue";
import { QueryObserver } from "@tanstack/vue-query";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { watch } from "vue";

export interface BindQueryOptions<T> {
  queryKey: readonly unknown[]
  queryFn: (ctx: { signal: AbortSignal }) => Promise<T>
  enabled: boolean
  staleTime?: number
  retry?: QueryObserverOptions<T>["retry"]
  /** Called once per failed fetch (QueryObserver has no onError option itself). */
  onError?: (error: unknown) => void
}

const DEFAULT_STALE_TIME = 5 * 60_000;

function toObserverOptions<T>(options: BindQueryOptions<T>): QueryObserverOptions<T> {
  const { onError: _onError, staleTime, ...rest } = options;
  return {
    ...rest,
    staleTime: staleTime ?? DEFAULT_STALE_TIME,
  };
}

function isSettled(result: QueryObserverResult): boolean {
  return result.status !== "pending" || result.fetchStatus === "idle";
}

/**
 * `loaded` turns true once `target` holds data of the current key and false again when the
 * key changes, so consumers can tell "not fetched yet" apart from an empty result.
 */
export function bindQueryToRef<T>(
  target: Ref<T>,
  options: () => BindQueryOptions<T>,
  fallback: T,
  fetching?: Ref<boolean>,
  loaded?: Ref<boolean>,
) {
  const observer = new QueryObserver<T>(getQueryClient(), toObserverOptions(options()));
  // errorUpdatedAt is a millisecond timestamp, so two failures in the same tick would be
  // deduplicated; errorUpdateCount increments per failed fetch.
  let notifiedErrorCount = 0;
  observer.subscribe((result) => {
    if (result.data !== undefined) {
      target.value = result.data;
      if (loaded) {
        loaded.value = true;
      }
    }
    if (fetching) {
      fetching.value = result.isFetching;
    }
    if (result.status !== "error" || result.errorUpdateCount === notifiedErrorCount) {
      return;
    }
    notifiedErrorCount = result.errorUpdateCount;
    options().onError?.(result.error);
  });
  let currentKey = JSON.stringify(options().queryKey);
  watch(options, (next) => {
    const key = JSON.stringify(next.queryKey);
    if (key !== currentKey) {
      currentKey = key;
      target.value = fallback;
      if (loaded) {
        loaded.value = false;
      }
    }
    observer.setOptions(toObserverOptions(next));
  }, { deep: false });

  /** Resolves once the current query has data, an error, or is disabled (idle pending). */
  function whenSettled(): Promise<void> {
    const current = observer.getCurrentResult();
    if (isSettled(current)) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const unsubscribe = observer.subscribe((result) => {
        if (!isSettled(result)) {
          return;
        }
        unsubscribe();
        resolve();
      });
    });
  }

  return { refetch: () => observer.refetch(), whenSettled };
}
